import { Autocomplete, TextField } from "@mui/material";
import { useAsync2 } from "nate-react-api-helpers";
import { useState } from "react";
import { api } from "../../api/API";
import { Task } from "../../api/Tasks";
import { panelFontSize } from "./panel/PanelSection";

// a title that matches no task can be created from the dropdown, so a task can be
// broken into its parts without leaving the page
type Option =
    | { kind: "task"; task: Task }
    | { kind: "create"; title: string };

// searching from the first keystroke returns most of the list and little of use, so
// the lookup holds off until the input narrows it down
export const minSearch = 5;

// searchReady says whether a term is enough to search on. A task number ("#293" or
// "293") is, from its first digit, and so is a permalink to a task (see
// taskPermalink). min replaces minSearch for a text term.
export function searchReady(term: string, min: number = minSearch) {
    return /^#?\d+$/.test(term) || isTaskPermalink(term) || term.length >= min;
}

function isTaskPermalink(term: string) {
    return /^https?:\/\/\S*[?&]task=\d+([&#]\S*)?$/.test(term);
}

// taskPermalink is a link that opens the task on its team's week, on the week it's
// due. The searches read the task back out of it.
export function taskPermalink(task: { id: number; team?: number; dueDate?: string | null }) {
    const params = new URLSearchParams({ mode: "week", task: String(task.id) });
    if (task.dueDate) params.set("date", task.dueDate);

    return window.location.origin + "/w/" + (task.team || "all") + "/tasks/calendar?" + params.toString();
}

// TaskPicker looks up an open task by name, or offers to make one under the name that
// was typed. Creating is left to the caller, which decides what the new task is.
export function TaskPicker(props: {
    placeholder: string;
    disabled?: boolean;

    // exclude leaves out the tasks the caller is already holding
    exclude: number[];

    // team narrows the search to one team. Without it every team the user is on is
    // searched.
    team?: number;

    // onPick takes a task that already exists, onCreate a title that matched none.
    // Without onCreate, no task is offered to be made.
    onPick: (task: Task) => void;
    onCreate?: (title: string) => void;
}) {
    const [search, setSearch] = useState("");

    const term = search.trim();
    const ready = searchReady(term);

    const options = useAsync2(input => {
        if (!input.ready) return Promise.resolve([] as Task[]);

        return api.tasks.list({
            search: input.search,
            completed: false,
            team: input.team,
        });
    }, { search: term, ready: ready, team: props.team }, [term, ready, props.team]);

    const available: Option[] = options.asList
        .filter(task => !props.exclude.includes(task.id))
        .map(task => ({ kind: "task", task: task }));

    return (
        <>
            <Autocomplete
                blurOnSelect
                autoHighlight
                size="small"
                disabled={props.disabled}
                options={available}
                value={null}
                inputValue={search}
                onInputChange={(_, value) => setSearch(value)}
                getOptionLabel={(option: Option) => option.kind === "create"
                    ? "Create: " + option.title
                    : "#" + option.task.id + " " + option.task.title}
                noOptionsText={ready
                    ? "No matching tasks"
                    : "Type " + minSearch + " characters to search"}
                // the server already searched; the create option leads so enter takes it
                filterOptions={list => {
                    if (!term || !props.onCreate) return list;

                    return ([{ kind: "create", title: term }] as Option[]).concat(list);
                }}
                onChange={(_, value: Option | null) => {
                    if (!value) return;
                    setSearch("");

                    if (value.kind === "create") props.onCreate && props.onCreate(value.title);
                    else props.onPick(value.task);
                }}
                renderInput={params => (
                    <TextField
                        {...params}
                        variant="standard"
                        placeholder={props.placeholder}
                        slotProps={{
                            input: {
                                ...params.InputProps,
                                disableUnderline: true,
                                style: { fontSize: panelFontSize },
                            },
                        }}
                    />
                )}
            />
            {options.LoadingOrErrorElement}
        </>
    );
}
